/*
 * The one owner of "which pets does this person have".
 *
 * Before this, the account's pets lived in four places — App's state, the
 * routed booking flow's state, the booking page's own picker list and a
 * cache in storage — plus a saved selection and a pre-login store, and
 * nothing owned them. Every change had to be broadcast by hand to each
 * place, and the ones that were forgotten became this week's bugs: a
 * deleted pet still in the picker, a pet made before signing in listed
 * twice or lost, a "create your first pet" prompt for someone with three.
 *
 * Now every page reads from here and every change writes here. The store
 * holds the account's pets (from the server) and the local pets made
 * before signing in (from the public store), knows whether it has asked
 * the server yet, and settles a sign-in in exactly one place: copy waiting
 * local pets into the account, then fetch the account's list.
 */
import { useSyncExternalStore } from 'react';
import { Pet, fetchCurrentUserPets } from './api';
import {
  addPublicPet,
  clearPublicPets,
  getCMSJWT,
  getCMSPets,
  getPublicPets,
  setPetsCache,
  setPublicPets
} from './auth.service';
import { storageService } from './storage.service';

export type PetsStatus = 'idle' | 'loading' | 'ready';

export interface PetsState {
  /** Pets on the signed-in account, as the server last gave them. */
  account: Pet[];
  /** Pets made before signing in, with temporary negative ids. */
  local: Pet[];
  /** 'ready' once the server has been asked at least once this session (or there is no session to ask for). */
  status: PetsStatus;
  signedIn: boolean;
  /** The account copies the last settle() made of pre-login pets — a page selects them, as the person made them to book. */
  copied: Pet[];
}

let state: PetsState = { account: [], local: [], status: 'idle', signedIn: false, copied: [] };
const listeners = new Set<() => void>();
let settling: Promise<Pet[]> | null = null;
/** The last settle that read the account's list: for which token, and when. */
let settledFor: { jwt: string; at: number } | null = null;
/** How long a page arriving may take the last settle's list as the server's (settleRecent). */
const RECENT_MS = 2 * 60 * 1000;

/** A pet made before signing in waits to be copied (the migration's own test, without loading its code). */
const publicPetsWaiting = (): boolean => {
  try {
    return storageService.getItem('create_from_public_pets') === 'true' || getPublicPets().length > 0;
  } catch {
    return true;
  }
};

const emit = () => listeners.forEach(l => l());
const set = (patch: Partial<PetsState>) => {
  state = { ...state, ...patch };
  emit();
};

/** Same pet by the only identity a pre-login pet has: its name and type. */
export const petKey = (p: Pet | { name?: string; type?: string }): string =>
  `${(p.name || '').trim().toLowerCase()}|${p.type || ''}`;

const isAccountPet = (p: Pet) => (p.id ?? 0) > 0;

const localWithIds = (raw: any[]): Pet[] =>
  raw.map((pet, index) => ({
    id: -(index + 1),
    name: pet.name,
    type: (pet.type as 'dog' | 'cat') || 'dog',
    breed: pet.breed,
    breedId: pet.breedId,
    gender: pet.gender,
    weight: pet.weight,
    age: pet.age,
    dob: pet.dob,
    aggression: pet.aggression,
    info: pet.info
  }));

const readLocalFromStorage = (): Pet[] => {
  try {
    return localWithIds(getPublicPets());
  } catch {
    return [];
  }
};

export const petsStore = {
  getState: (): PetsState => state,

  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  /** Every pet a picker should list: the account's, then local ones not already on the account. */
  all(): Pet[] {
    const onAccount = new Set(state.account.map(petKey));
    return [...state.account, ...state.local.filter(l => !onAccount.has(petKey(l)))];
  },

  /** Re-read the pre-login pets from storage (after a signed-out create/edit/delete). */
  readLocal(): Pet[] {
    const local = readLocalFromStorage();
    set({ local });
    return local;
  },

  /** A pet made while signed out. Returns it with its temporary id. */
  addLocal(pet: Omit<Pet, 'id'>): Pet {
    addPublicPet(pet as any);
    const local = petsStore.readLocal();
    return local.find(l => petKey(l) === petKey(pet)) ?? { ...pet, id: -local.length };
  },

  updateLocal(previous: Pet, next: Omit<Pet, 'id'>): void {
    const raw = getPublicPets();
    const idx = raw.findIndex((p: any) => petKey(p) === petKey(previous));
    if (idx !== -1) {
      raw[idx] = { ...raw[idx], ...next };
      setPublicPets(raw);
    }
    petsStore.readLocal();
  },

  removeLocal(pet: Pet): void {
    const raw = getPublicPets().filter((p: any) => petKey(p) !== petKey(pet));
    if (raw.length === 0) clearPublicPets();
    else setPublicPets(raw);
    petsStore.readLocal();
  },

  /**
   * The one place a sign-in settles pets. Signed out: the local pets are
   * all there is. Signed in: copy any waiting local pets into the account
   * (the migration dedups by name and type, drops each pet from the local
   * store as it is created, and runs once at a time), then fetch the
   * account's list. Concurrent callers share one run. Safe to call from
   * every page that mounts; the migration is a no-op when nothing waits.
   */
  settle(): Promise<Pet[]> {
    if (settling) return settling;
    if (!getCMSJWT()) {
      set({ signedIn: false, account: [], local: readLocalFromStorage(), status: 'ready', copied: [] });
      return Promise.resolve([]);
    }
    set({ signedIn: true, status: state.status === 'ready' ? 'ready' : 'loading' });
    const jwt = getCMSJWT() as string;
    settling = (async () => {
      let copied: Pet[] = [];
      // The copy's code is its own chunk: loading it only when a pet waits
      // keeps a round trip off the front of every signed-in pets read.
      if (publicPetsWaiting()) {
        try {
          const { migratePublicPetsIfNeeded } = await import('./pet-migration.service');
          copied = (await migratePublicPetsIfNeeded()).createdPets;
        } catch (err) {
          console.error('Copying pre-login pets failed:', err);
        }
      }
      try {
        const account = await fetchCurrentUserPets();
        setPetsCache(account);
        settledFor = { jwt, at: Date.now() };
        set({ signedIn: true, account, local: readLocalFromStorage(), status: 'ready', copied });
        return account;
      } catch (err) {
        settledFor = null;
        console.error('Loading the account’s pets failed:', err);
        // What we last knew (this session, else the cache). An empty list
        // here is not "no pets", so the store is not 'ready' on it — a
        // "create your first pet" prompt must never fire on a failed fetch.
        const account: Pet[] = state.account.length > 0 ? state.account : getCMSPets();
        set({
          signedIn: true,
          account,
          local: readLocalFromStorage(),
          copied,
          status: account.length > 0 ? 'ready' : state.status === 'ready' ? 'ready' : 'idle'
        });
        return account;
      } finally {
        settling = null;
      }
    })();
    return settling;
  },

  /**
   * settle() for a page arriving: when the account's list was read moments
   * ago for this same sign-in (the home page's settle, then the services
   * step's on arrival), that list stands and nothing is read again. It
   * answers as a second settle did: nothing new copied (`copied` empty).
   * Any pre-login pet waiting, another token, a failed or old read: a real
   * settle. After a create or an edit, call settle() itself: it always
   * reads the server's list.
   */
  settleRecent(): Promise<Pet[]> {
    if (settling) return settling;
    const jwt = getCMSJWT();
    const recent =
      !!jwt &&
      settledFor?.jwt === jwt &&
      Date.now() - settledFor.at < RECENT_MS &&
      state.signedIn &&
      state.status === 'ready' &&
      !publicPetsWaiting();
    if (!recent) return petsStore.settle();
    if (state.copied.length > 0) set({ copied: [] });
    return Promise.resolve(state.account);
  },

  /**
   * A sign-out. Nothing about the account may survive it in memory — on a
   * shared phone the next person opened the pet picker and saw the last
   * person's pets. The store goes back to "signed out, no pets".
   */
  reset(): void {
    settling = null;
    settledFor = null;
    set({ account: [], local: [], status: 'ready', signedIn: false, copied: [] });
  },

  /** After a create or edit on the account: the server's list, or the one pet meanwhile. */
  upsertAccount(pet: Pet): void {
    if (!isAccountPet(pet)) return;
    const account = state.account.some(p => p.id === pet.id)
      ? state.account.map(p => (p.id === pet.id ? { ...p, ...pet } : p))
      : [...state.account, pet];
    setPetsCache(account);
    set({ account });
  },

  removeAccount(id: number): void {
    const account = state.account.filter(p => p.id !== id);
    setPetsCache(account);
    set({ account });
  },

  /**
   * A selection made earlier, brought up to date with what the store holds
   * now: a pre-login pet (temporary id) becomes its copy on the account,
   * a pet that no longer exists anywhere is dropped, and nothing is listed
   * twice. This is what keeps the chips, the picker and a saved booking
   * agreeing after a sign-in, a copy, an edit or a delete.
   */
  reconcileSelection(selected: Pet[]): Pet[] {
    const all = petsStore.all();
    const byId = new Map<number, Pet>();
    all.forEach(p => {
      if (p.id != null) byId.set(p.id, p);
    });
    const byKey = new Map<string, Pet>();
    all.forEach(p => {
      if (isAccountPet(p) || !byKey.has(petKey(p))) byKey.set(petKey(p), p);
    });
    const seen = new Set<number>();
    const out: Pet[] = [];
    selected.forEach(s => {
      const match = (isAccountPet(s) ? byId.get(s.id as number) : undefined) ?? byKey.get(petKey(s));
      if (!match || match.id == null || seen.has(match.id)) return;
      seen.add(match.id);
      out.push(match);
    });
    return out;
  },

  /** True when the two selections name the same pets in the same order. */
  sameSelection(a: Pet[], b: Pet[]): boolean {
    return a.length === b.length && a.every((p, i) => p.id === b[i].id);
  }
};

/** Subscribe a component to the store. */
export const usePets = (): PetsState =>
  useSyncExternalStore(petsStore.subscribe, petsStore.getState, petsStore.getState);
